/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// React
import React, {useState, useEffect, useRef} from 'react'
import {useHistory, useLocation} from 'react-router-dom'

// PWA Kit OOTB Componets
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {
    Box,
    Link,
    Skeleton,
    useDisclosure,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'
import StoreLocatorModal from '@salesforce/retail-react-app/app/components/store-locator-modal'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import RedirectWithStatus from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/redirect-with-status'

// Custom Components
import OfflineBanner from '../offline-banner'
import Header from '../header'
import CheckoutHeader from '../../pages/checkout/partials/checkout-header'
import Footer from '../footer'
import {DrawerMenu} from '../drawer-menu'
import AboveHeader from './partials/above-header'
import {CountryDispatch} from '../../pages/country-dispatch'
import CountrySelectorModal from '../country-selector-modal'
import CookieConsentBanner from '../cookie-consent-banner'
import SeoLinks from '../seo-links'

// Commerce SDK Imports
import {
    useAccessToken,
    useCategory,
    useShopperBasketsMutation,
    useShopperContext,
    useUsid
} from '@salesforce/commerce-sdk-react'

// Chakra
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider} from '../../contexts'
import {PasswordProvider} from '../../custom-contexts'
import {IPProvider} from '../../custom-contexts'
import {ConversionRateProvider} from '../../custom-contexts'

// PWA Kit OOTB Hooks
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '../../hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'

// Custom Hooks
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {useDntNotification} from '@salesforce/retail-react-app/app/hooks/use-dnt-notification'
import {AddToCartModalProvider} from './../../hooks/use-add-to-cart-modal'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import useUpdateShopperContext from '../../hooks/use-update-shopper-context'
import useResetShippingAddress from '../../hooks/use-reset-shipping-address'
import useSiteType from '../../hooks/use-site-type'
import useUTMParameters from '../../hooks/use-utm-parameters'
import useRiskified from '../../hooks/use-riskified'

// Utils
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {watchOnlineStatus, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {fetchTranslations} from '@salesforce/retail-react-app/app/utils/locale'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getShipToCountry, resolveCountryFromUrl} from '../../utils/ship-to-country-utils'
import {
    getLocalStorageJSONItem,
    setLocalStorageJSONItem,
    cookieSettings,
    getAppDomain
} from '../../utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getDefaultShipment, useInitializeBasketMgr} from '../../utils/basket-utils'

// Third Party
import {IntlProvider, FormattedMessage} from 'react-intl'
import {useQuery} from '@tanstack/react-query'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'

// Constants
import {
    DEFAULT_SITE_TITLE,
    THEME_COLOR,
    CAT_MENU_DEFAULT_ROOT_CATEGORY,
    DEFAULT_LOCALE,
    ACTIVE_DATA_ENABLED
} from '@salesforce/retail-react-app/app/constants'
import {
    CAT_MENU_DEFAULT_NAV_SSR_DEPTH,
    GTM_SCRIPT_URL,
    GTM_NO_SCRIPT_URL,
    EU_DOMESTIC_COUNTRY_CODES,
    OUTLET_ENTRANCE_URL,
    OUTLET_LIMITATION_EXCEPTIONS,
    OUTLET_DISPATCH_URL,
    OUTLET_SESSION_PARAM,
    UNLIMITED_OUTLET_SESSION
} from '../../constants'

// Styles
import './global.css'

import {getGlobalRedirectionURL} from '../../utils/seo-utils'
import {useOutletCampaignStatus} from '../../utils/outlet-utils'
import OutletEntrance from '../../pages/outlet-entrance'

const App = (props) => {
    const {children} = props
    const {app: appConfig} = getConfig()
    const GTM_ID = appConfig.GTM_ID
    const appOrigin = useAppOrigin()
    const frontAppDomain = getAppDomain()
    const {site, locale, buildUrl} = useMultiSite()
    const {getTokenWhenReady} = useAccessToken()
    const {usid} = useUsid()
    const {data: categoriesTree} = useCategory({
        parameters: {
            id: CAT_MENU_DEFAULT_ROOT_CATEGORY,
            levels: CAT_MENU_DEFAULT_NAV_SSR_DEPTH,
            c_isMenu: true
        }
    })
    const {riskifiedBeacon, riskifiedDomain} = useRiskified(site?.id)
    const basketMgr = useInitializeBasketMgr()

    useEffect(() => {
        const updateLocale = async () => {
            try {
                const accessToken = await getTokenWhenReady()
                const result = await basketMgr.updateProductLineItemsLocale(
                    accessToken,
                    site.id,
                    locale.id
                )
                if (result?.updated) {
                    await refetchBasket()
                }
            } catch (error) {
                console.error('Error updating locale:', error)
            }
        }

        if (locale?.id) {
            updateLocale()
        }
    }, [locale?.id, site.id])
    // Get IP Address from the server if the app is running on the server.
    const {data: ipData} = useQuery({
        queryKey: ['userIP'],
        queryFn: async () => {
            const token = await getTokenWhenReady()
            const response = await fetch(
                `${appOrigin}/block-status?localeId=${locale?.id}&siteId=${site?.id}&orgId=${appConfig?.commerceAPI?.parameters.organizationId}`,
                {
                    method: 'GET',
                    headers: {
                        authorization: token
                    }
                }
            )
            const result = await response.json()

            return result
        },
        enabled: !isServer
    })

    const [areCookiesHandled, setAreCookiesHandled] = useState(false)

    // logic for showing/hiding the current modal
    const [activeModal, setActiveModal] = useState(null)
    const [isSetShipToCountryEnabled, setIsSetShipToCountryEnabled] = useState(null)
    const cspNonce = appConfig?.commerceAPI?.parameters?.cspNonce
    // GTM Integration
    useEffect(() => {
        if (!isServer && cspNonce) {
            ;(function (w, d, s, l, i) {
                w[l] = w[l] || []
                w[l].push({
                    'gtm.start': new Date().getTime(),
                    event: 'gtm.js'
                    //'gtm.nonce': `${cspNonce}`
                })
                var f = d.getElementsByTagName(s)[0],
                    j = d.createElement(s),
                    dl = l != 'dataLayer' ? '&l=' + l : ''
                j.async = true
                j.nonce = `${cspNonce}`
                j.src = GTM_SCRIPT_URL + i + dl
                f.parentNode.insertBefore(j, f)
            })(window, document, 'script', 'dataLayer', GTM_ID)
        }
    }, [cspNonce])

    const updateShippingAddressForShipment = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )

    /**
     * Sets the local storage items related to cookie consent.
     *
     * This function updates the local storage to indicate whether cookies have been handled
     * and whether the user has accepted cookies. It stores these values as strings.
     *
     * @param {boolean} consent - A boolean value indicating whether the user has consented to cookies.
     *                             If true, 'cookiesAccepted' is set to 'true'; otherwise, it is set to an empty string.
     */
    const setLocalStorage = (consent) => {
        setLocalStorageJSONItem('cookiesHandled', 'true')
        setLocalStorageJSONItem('cookiesAccepted', consent ? 'true' : '')
    }

    /**
     * Handles the user's cookie consent decision.
     *
     * This function is triggered when the user provides their consent for cookies.
     * If consent is given, it updates the state to indicate that cookies have been handled
     * and configures the settings for various types of cookies, including Google Analytics (ga),
     * advertising (ad), and social networking (sn) cookies.
     *
     * @param {boolean} consent - A boolean value indicating whether the user has consented to cookies.
     */
    const handleCookieConsent = (consent) => {
        if (!isServer) {
            setAreCookiesHandled(true)

            const consentTypes = ['ga', 'ad', 'sn']
            const consentValue = consent ? 'true' : 'false'

            consentTypes.forEach((cookieType) => cookieSettings(consentValue, cookieType))
            setLocalStorage(consent)
        }
    }

    useEffect(() => {
        if (getLocalStorageJSONItem('cookiesHandled')) {
            handleCookieConsent(!!getLocalStorageJSONItem('cookiesAccepted'))
        } else {
            setAreCookiesHandled(false)
        }
    }, [])

    useEffect(() => {
        if (!isServer) {
            window.dataLayer = window.dataLayer || []
        }
    }, [])

    // update the shopper context when user logs in/logs out
    useUpdateShopperContext()
    // reset shipping address if ship to country is different than the shipping address country
    useResetShippingAddress()

    const activeData = useActiveData()
    const history = useHistory()
    const location = useLocation()
    const authModal = useAuthModal()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const dntNotification = useDntNotification()
    const {isUS, isOutlet} = useSiteType()

    const {updateShopperContextAfterLogout} = useUpdateShopperContext()

    const shopperContext = useShopperContext(
        {
            parameters: {usid: usid}
        },
        {enabled: Boolean(!isServer && usid)}
    )
    // handle UTM parameters with local storage
    useUTMParameters()

    useEffect(() => {
        // if we have a new accessToken and the shopper context is not yet set - we are forcing to set it
        if (!isOutletEntrancePage && !activeModal && shopperContext.error) {
            updateShopperContextAfterLogout(true)
        }
    }, [shopperContext.error])

    const countryCode = shopperContext?.data?.geoLocation?.countryCode
    const country = resolveCountryFromUrl(location?.search)
    const isCountryNotDefinedInUrl = location?.pathname === '/' && country === null && !isOutlet
    const isOutletDispatchPage = location?.pathname === buildUrl(OUTLET_DISPATCH_URL) && isOutlet
    const isOutletEntrancePage = location?.pathname === buildUrl(OUTLET_ENTRANCE_URL) && isOutlet
    const isPDP =
        location?.pathname.includes('/product/') ||
        location?.pathname.includes('/ps/') ||
        location?.pathname.includes('/p/')

    const {setShipToCountry} = useShipToCountry()
    const shipToCountry = getShipToCountry(countryCode, isUS)

    useEffect(() => {
        setShipToCountry(shipToCountry)
        refetchBasket()
    }, [shipToCountry?.id])

    // Riskified integration,
    // this is needed to allow the beacon to load after the page has loaded
    useEffect(() => {
        function riskifiedBeaconLoad() {
            const store_domain = riskifiedDomain
            const session_id = riskifiedBeacon
            const url =
                (document.location.protocol === 'https:' ? 'https://' : 'http://') +
                'beacon.riskified.com?shop=' +
                store_domain +
                '&sid=' +
                session_id
            const s = document.createElement('script')
            s.type = 'text/javascript'
            s.async = true
            s.src = url

            const x = document.getElementsByTagName('script')[0]
            x.parentNode.insertBefore(s, x)
        }
        if (
            !isServer &&
            riskifiedDomain &&
            riskifiedBeacon &&
            shipToCountry?.id &&
            !EU_DOMESTIC_COUNTRY_CODES.includes(shipToCountry?.id)
        ) {
            if (window.attachEvent) {
                window.attachEvent('onload', riskifiedBeaconLoad)
            } else {
                window.addEventListener('load', riskifiedBeaconLoad, false)
            }

            // Cleanup function to remove the event listener if the component unmounts
            return () => {
                if (window.detachEvent) {
                    window.detachEvent('onload', riskifiedBeaconLoad)
                } else {
                    window.removeEventListener('load', riskifiedBeaconLoad, false)
                }
            }
        }
    }, [riskifiedDomain, riskifiedBeacon, shipToCountry?.id])

    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')

    // Drawer Menu
    const {isOpen, onOpen, onClose} = useDisclosure()
    const drawerMenuTriggerRef = useRef()

    const {
        isOpen: isOpenStoreLocator,
        onOpen: onOpenStoreLocator,
        onClose: onCloseStoreLocator
    } = useDisclosure()

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', locale?.id],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(locale?.id, appOrigin)
        },
        keepPreviousData: true,
        enabled: isServer || (!isServer && (isCountryNotDefinedInUrl || isOutletDispatchPage))
    })

    // Used to conditionally render for checkout page
    const isCheckout = /\/checkout$/.test(location?.pathname)

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = shipToCountry?.currency || locale?.preferredCurrency || l10n?.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: basket, refetch: refetchBasket} = useCurrentBasket()

    // export conversion rate fields from basket to conversion rate provider
    const conversionRateFields = {
        conversionRate: basket?.c_conversionRate,
        lcpMultiplier: basket?.c_lcpMultiplier,
        paymentCurrencyCode: basket?.c_paymentCurrencyCode,
        usePriceConverter: basket?.c_usePriceConverter || false,
        isInternationalOrder: basket?.c_isInternationalOrder || false
    }

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    const isOutletException = OUTLET_LIMITATION_EXCEPTIONS.some((exception) => {
        return location.pathname.includes(buildUrl(exception))
    })

    // handling outlet redirections
    const {isCampaignActive: isOutletOpened, isLoading: isCampaignStatusLoading} =
        useOutletCampaignStatus()

    useEffect(() => {
        if (
            isOutlet &&
            ((!isCampaignStatusLoading && isOutletOpened === false) ||
                getLocalStorageJSONItem(OUTLET_SESSION_PARAM) !== UNLIMITED_OUTLET_SESSION) &&
            !isOutletException
        ) {
            window.location.href = buildUrl(OUTLET_ENTRANCE_URL)
        }
    }, [location, isCampaignStatusLoading, isOutletOpened])

    useEffect(() => {
        const updateBasketCurrency = async () => {
            // Check if the basket currency differs from the current locale currency
            if (basket?.currency && basket.currency !== currency) {
                // Update the basket with the new currency
                updateBasket.mutate({
                    parameters: {basketId: basket.basketId},
                    body: {currency}
                })

                // Retrieve the default shipment for the basket
                const defaultShipment = getDefaultShipment(basket)

                // Reset the shipping address for the default shipment
                await updateShippingAddressForShipment.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        shipmentId: defaultShipment?.shipmentId
                    },
                    body: {}
                })
            }
        }

        updateBasketCurrency()
    }, [shipToCountry?.currency, shipToCountry?.id])
    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email &&
            basket?.basketId
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
    }, [
        customer?.isRegistered,
        customer?.email,
        basket?.customerInfo?.email,
        basket && basket?.basketId
    ])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    useEffect(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose()
    }, [location])

    const onAccountClick = () => {
        // Link to account page if registered; Header component will show auth modal for guest users
        const path = buildUrl('/account')
        history.push(path)
    }

    const onWishlistClick = () => {
        // Link to wishlist page if registered; Header component will show auth modal for guest users
        const path = buildUrl('/account/wishlist')
        history.push(path)
    }

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()
    }, [location])

    /**
     * useEffect hook to control the display of application modals based on various state conditions.
     *
     * This effect determines which modal should be active by evaluating the following conditions:
     * - If the country is defined in the URL, the user is not on the outlet dispatch page, and the "Set Ship To Country" feature is disabled,
     *   the 'countrySelector' modal is activated.
     * - If cookies have not been handled, the 'cookieConsent' modal is activated.
     * - If none of the above conditions are met, no modal is shown.
     *
     * The effect re-runs whenever any of the following dependencies change:
     * - areCookiesHandled: Boolean indicating if cookie consent has been managed.
     * - isCountryNotDefinedInUrl: Boolean indicating if the country is missing from the URL.
     * - isOutletDispatchPage: Boolean indicating if the current page is the outlet dispatch page.
     * - isSetShipToCountryEnabled: Boolean indicating if the "Set Ship To Country" feature is enabled.
     *
     * @function
     * @memberof App
     */
    useEffect(() => {
        /* ADD HERE THE CONDITION FOR THE OTHER MODALS */
        if (
            !isCountryNotDefinedInUrl &&
            !isOutletDispatchPage &&
            !isOutletEntrancePage &&
            !isSetShipToCountryEnabled
        ) {
            setActiveModal('countrySelector')
        } else if (!areCookiesHandled && !isOutletEntrancePage) {
            setActiveModal('cookieConsent')
        } else {
            setActiveModal(null)
        }
    }, [
        areCookiesHandled,
        isCountryNotDefinedInUrl,
        isOutletDispatchPage,
        isOutletEntrancePage,
        isSetShipToCountryEnabled
    ])

    /*
        Since we need to create canonical tags on every page manage it in the _app component.
        When it is needed overwrite the default canonical tag in the component itself by adding <Helmet> like below when it is needed.
        <Helmet>
            <link rel="canonical" href="/overwritten-canonical-tag-goes-here" />
        </Helmet>
    */
    const isSearchPage = location.pathname.includes('/search')
    const [defaultCanonicalTag, setDefaultCanonicalTag] = useState(
        new URL(location.pathname, frontAppDomain).toString()
    )

    useEffect(() => {
        setDefaultCanonicalTag(new URL(location.pathname, frontAppDomain).toString())
    }, [location])

    const globalRedirectionUrl = getGlobalRedirectionURL(location)
    if (globalRedirectionUrl) {
        return <RedirectWithStatus status={301} to={globalRedirectionUrl} />
    }

    // Display a skeleton while outlet campaign is retrieved on outlet sites
    if (isOutlet && !isOutletEntrancePage && !isOutletException && isCampaignStatusLoading) {
        return <Skeleton height="100vh" width="100%" />
    }

    return (
        <Box className="sf-app" {...styles.container}>
            <StorefrontPreview getToken={getTokenWhenReady}>
                <Helmet>
                    {ACTIVE_DATA_ENABLED && (
                        <script
                            src={getAssetUrl('static/head-active_data.js')}
                            id="headActiveData"
                            type="text/javascript"
                            defer
                        ></script>
                    )}
                    {/*  <Site24x7 /> */}
                    {appConfig.ENABLE_SITE24X7 && (
                        <script
                            src={getAssetUrl('static/24x7.js')}
                            id="24x7_script"
                            type="text/javascript"
                            defer
                        ></script>
                    )}
                    <script>
                        {`
                    if (window.trustedTypes) {
                        window.trustedTypes.createPolicy('default', {
                            createHTML: (string) => string,
                            createScript: (string) => string,
                            createScriptURL: (string) => string
                        });
                    }
                    `}
                    </script>
                    <html lang={locale?.id} />
                </Helmet>
                {!isPDP && <SeoLinks />}
                <IntlProvider
                    onError={(err) => {
                        if (!messages) {
                            // During the ssr prepass phase the messages object has not loaded, so we can suppress
                            // errors during this time.
                            return
                        }
                        if (err.code === 'MISSING_TRANSLATION') {
                            // NOTE: Remove the console error for missing translations during development,
                            // as we knew translations would be added later.
                            logger.warn('Missing translation', {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message
                                }
                            })
                            return
                        } else if (err.code === 'FORMAT_ERROR') {
                            logger.warn('Format error', {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message
                                }
                            })
                            return
                        } else {
                            logger.warn(err.code, {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message
                                }
                            })
                            return
                        }
                    }}
                    locale={locale?.id}
                    messages={messages}
                    // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                    // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                    // - "extract-default-translations"
                    // - "compile-translations:pseudo"
                    defaultLocale={DEFAULT_LOCALE}
                >
                    <CurrencyProvider currency={currency}>
                        <PasswordProvider>
                            <ConversionRateProvider conversionRateFields={conversionRateFields}>
                                <IPProvider
                                    ip={ipData?.clientIp}
                                    hasFetched={!!ipData?.clientIp}
                                    isIPBlocked={ipData?.isIPBlocked}
                                >
                                    <Seo>
                                        {!isSearchPage && !isPDP && (
                                            <link rel="canonical" href={defaultCanonicalTag} />
                                        )}
                                        <meta name="theme-color" content={THEME_COLOR} />
                                        <meta
                                            name="apple-mobile-web-app-title"
                                            content={DEFAULT_SITE_TITLE}
                                        />

                                        {/* Favicon list */}
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            href={getAssetUrl(
                                                'static/img/favicon/favicon-16x16.png'
                                            )}
                                            sizes="16x16"
                                        />
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            href={getAssetUrl(
                                                'static/img/favicon/favicon-32x32.png'
                                            )}
                                            sizes="32x32"
                                        />
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            href={getAssetUrl(
                                                'static/img/favicon/favicon-96x96.png'
                                            )}
                                            sizes="96x96"
                                        />
                                        {/* older-iPhone */}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/favicon/apple-touch-icon.png'
                                            )}
                                        />
                                        {/* iPhone-6-Plus */}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/favicon/apple-touch-icon-180x180.png'
                                            )}
                                            sizes="180x180"
                                        />
                                        {/* iPad-Retina */}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/favicon/apple-touch-icon-152x152.png'
                                            )}
                                            sizes="152x152"
                                        />
                                        {/* iPad-Pro */}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/favicon/apple-touch-icon-167x167.png'
                                            )}
                                            sizes="167x167"
                                        />
                                        <link
                                            rel="manifest"
                                            href={getAssetUrl('static/manifest.json')}
                                        />

                                        {/* Styles from external tools */}
                                        {/* Use media queries to load CSS conditionally to improve performance. */}
                                        <link
                                            rel="stylesheet"
                                            type="text/css"
                                            href={getAssetUrl('static/global.css')}
                                        />
                                        {!isCheckout && (
                                            <link
                                                rel="stylesheet"
                                                type="text/css"
                                                href={getAssetUrl(
                                                    'static/third-party/slick-carousel/slick/slick.css'
                                                )}
                                            />
                                        )}
                                        {!isCheckout && (
                                            <link
                                                rel="stylesheet"
                                                type="text/css"
                                                href={getAssetUrl(
                                                    'static/third-party/slick-carousel/slick/slick-theme.css'
                                                )}
                                            />
                                        )}
                                    </Seo>

                                    <ScrollToTop />

                                    <Box id="app" display="flex" flexDirection="column" flex={1}>
                                        {activeModal === 'cookieConsent' && (
                                            <CookieConsentBanner
                                                handleCookieConsent={handleCookieConsent}
                                            />
                                        )}
                                        {/* TODO: Change href value when accessibility page is created */}
                                        <Link href="#" zIndex="skipLink" {...styles.hiddenLink}>
                                            <FormattedMessage
                                                defaultMessage="ACCESSIBILITY"
                                                id="skip_link.accessibility"
                                            />
                                        </Link>
                                        <SkipNavLink zIndex="skipLink" {...styles.hiddenLink}>
                                            <FormattedMessage
                                                defaultMessage="SKIP TO MAIN CONTENT"
                                                id="skip_link.skip_content"
                                            />
                                        </SkipNavLink>

                                        {(isCountryNotDefinedInUrl || isOutletDispatchPage) && (
                                            <SkipNavContent>
                                                <CountryDispatch></CountryDispatch>
                                                <Footer simplified={true} />
                                            </SkipNavContent>
                                        )}
                                        {!isCountryNotDefinedInUrl && !isOutletDispatchPage && (
                                            <>
                                                <StoreLocatorModal
                                                    isOpen={isOpenStoreLocator}
                                                    onClose={onCloseStoreLocator}
                                                />

                                                {activeModal === 'countrySelector' && (
                                                    <CountrySelectorModal
                                                        setIsSetShipToCountryEnabled={
                                                            setIsSetShipToCountryEnabled
                                                        }
                                                    />
                                                )}

                                                <Box {...styles.headerWrapper}>
                                                    {!isOutletEntrancePage &&
                                                        (!isCheckout ? (
                                                            <>
                                                                <AboveHeader />
                                                                <Header
                                                                    onMenuClick={onOpen}
                                                                    onMyAccountClick={
                                                                        onAccountClick
                                                                    }
                                                                    onWishlistClick={
                                                                        onWishlistClick
                                                                    }
                                                                    onStoreLocatorClick={
                                                                        onOpenStoreLocator
                                                                    }
                                                                    drawerMenuTriggerRef={
                                                                        drawerMenuTriggerRef
                                                                    }
                                                                >
                                                                    <DrawerMenu
                                                                        isOpen={isOpen}
                                                                        onClose={onClose}
                                                                        root={categoriesTree}
                                                                        itemsKey="categories"
                                                                        itemsCountKey="onlineSubCategoriesCount"
                                                                        drawerMenuTriggerRef={
                                                                            drawerMenuTriggerRef
                                                                        }
                                                                    />
                                                                </Header>
                                                            </>
                                                        ) : (
                                                            <CheckoutHeader />
                                                        ))}
                                                </Box>
                                                {!isOnline && <OfflineBanner />}
                                                <AddToCartModalProvider>
                                                    <SkipNavContent
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            flex: 1,
                                                            outline: 0
                                                        }}
                                                    >
                                                        <Box
                                                            as="main"
                                                            id="app-main"
                                                            role="main"
                                                            display="flex"
                                                            flexDirection="column"
                                                            flex="1"
                                                        >
                                                            <OfflineBoundary isOnline={false}>
                                                                {isOutletEntrancePage ? (
                                                                    <OutletEntrance
                                                                        isOutletOpened={
                                                                            isOutletOpened
                                                                        }
                                                                        isCampaignStatusLoading={
                                                                            isCampaignStatusLoading
                                                                        }
                                                                    />
                                                                ) : (
                                                                    children
                                                                )}
                                                            </OfflineBoundary>
                                                        </Box>
                                                    </SkipNavContent>
                                                    {!isOutletEntrancePage && <Footer />}
                                                    <AuthModal {...authModal} />
                                                    {/* Commenting out as for the moment DNT is set to false by default */}
                                                    {/* <DntNotification {...dntNotification} /> */}
                                                </AddToCartModalProvider>
                                            </>
                                        )}
                                    </Box>
                                </IPProvider>
                            </ConversionRateProvider>
                        </PasswordProvider>
                    </CurrencyProvider>
                </IntlProvider>
                {ACTIVE_DATA_ENABLED && (
                    <script
                        type="text/javascript"
                        src={getAssetUrl('static/dwanalytics-22.2.js')}
                        id="dwanalytics"
                        defer
                        onLoad={trackPage}
                    ></script>
                )}
                {ACTIVE_DATA_ENABLED && (
                    <script
                        src={getAssetUrl('static/dwac-21.7.js')}
                        type="text/javascript"
                        id="dwac"
                        defer
                    ></script>
                )}
                <noscript>
                    <iframe
                        src={`${GTM_NO_SCRIPT_URL}${GTM_ID}`}
                        height="0"
                        width="0"
                        style={{display: 'none', visibility: 'hidden'}}
                        title="Google Tag Manager"
                    ></iframe>
                </noscript>
            </StorefrontPreview>
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node
}

export default App
